<template>
  <div class="flex justify-center gap-2">
    <PaginationButton
      v-if="showFirstLast"
      :disabled="paginationInfo.current === paginationInfo.first"
      :to="getPath(paginationInfo.first)"
      @click="selectPage(paginationInfo.first)"
    >
      <IconComponent icon="angle-double-left" />
    </PaginationButton>

    <PaginationButton
      :disabled="!paginationInfo.hasPrevious"
      :to="getPath(paginationInfo.current - 1)"
      @click="selectPage(paginationInfo.current - 1)"
    >
      <IconComponent icon="angle-left" />
    </PaginationButton>

    <PaginationButton
      v-for="index in getIndexes"
      :key="index"
      :active="index === paginationInfo.current"
      :to="getPath(index)"
      :class="{
        'block md:hidden':
          paginationInfo.first === paginationInfo.current ||
          paginationInfo.current === paginationInfo.last
            ? Math.abs(paginationInfo.current - index) > increment
            : Math.abs(paginationInfo.current - index) > increment - 1, // On desktop show one less
      }"
      @click="selectPage(index)"
    >
      {{ index }}
    </PaginationButton>
    <PaginationButton
      :disabled="!paginationInfo.hasNext"
      :to="getPath(paginationInfo.current + 1)"
      @click="selectPage(paginationInfo.current + 1)"
    >
      <IconComponent icon="angle-right" />
    </PaginationButton>

    <PaginationButton
      v-if="showFirstLast"
      :disabled="paginationInfo.current === paginationInfo.last"
      :to="getPath(paginationInfo.last)"
      @click="selectPage(paginationInfo.last)"
    >
      <IconComponent icon="angle-double-right" />
    </PaginationButton>
  </div>
</template>

<script>
import PaginationButton from "~/components/_refactored/common/PaginationButton.vue";

export default {
  components: {
    PaginationButton,
  },
  props: {
    paginationInfo: {
      type: Object,
      required: true,
    },
    increment: {
      type: Number,
      default: 2,
    },
    showFirstLast: {
      type: Boolean,
      default: false,
    },
    pushRoute: {
      type: Boolean,
      default: true,
    },
    /**
     * Use hash-based pagination (#page=X) for SEO.
     * Set to false for conductor/admin pages that need query params.
     */
    useHashParams: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    getCurrentPageRangeTwo() {
      let start = this.paginationInfo.current - this.increment;
      let end = this.paginationInfo.current + this.increment;

      if (start < 1) {
        start = 1;
        end += 1;
        if (end > this.paginationInfo.last) {
          end = this.paginationInfo.last;
        }
      }
      if (end > this.paginationInfo.last) {
        end = this.paginationInfo.last;
        start -= 1;
        if (start < 1) {
          start = 1;
        }
      }

      return { start, end };
    },
    start() {
      return this.getCurrentPageRangeTwo.start;
    },
    end() {
      return this.getCurrentPageRangeTwo.end;
    },
    getIndexes() {
      return this.$helpers.arrayRange(this.start, this.end, 1);
    },
  },
  methods: {
    selectPage(page) {
      this.$trackers.trackClick("PaginationButton", {
        page,
      });

      this.$emit("pageChange", page);
    },
    /**
     * Generate URL for pagination
     * @param {number} page - Page number
     * @returns {string|Object|null} URL with hash, route object with query, or null
     */
    getPath(page) {
      if (!this.pushRoute) {
        return null;
      }

      // Query-based pagination (for conductor/admin pages)
      if (!this.useHashParams) {
        return {
          query: { ...this.$route.query, page },
        };
      }

      // Hash-based pagination (for SEO - default)
      // Build query string from existing params, excluding 'page'
      const currentQuery = { ...this.$route.query };
      delete currentQuery.page;

      const queryString = Object.keys(currentQuery).length
        ? "?" +
          Object.entries(currentQuery)
            .map(
              ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
            )
            .join("&")
        : "";

      // Extract existing hash params
      const hashParams = {};
      const hash = this.$route.hash || "";
      const hashContent = hash.startsWith("#") ? hash.slice(1) : hash;
      
      if (hashContent) {
        const pairs = hashContent.split("&");
        for (const pair of pairs) {
          const [key, value] = pair.split("=");
          if (key && value !== undefined && key !== "page") {
            hashParams[key] = decodeURIComponent(value);
          }
        }
      }
      
      // Build new hash with page and preserve existing params like 'keyword'
      const hashParts = [];
      if (page > 1) {
        hashParts.push(`page=${page}`);
      }
      
      // Add other hash params (like keyword) that should be preserved
      for (const [key, value] of Object.entries(hashParams)) {
        if (value) {
          hashParts.push(`${key}=${encodeURIComponent(value)}`);
        }
      }
      
      const newHash = hashParts.length ? `#${hashParts.join("&")}` : "";

      return `${this.$route.path}${queryString}${newHash}`;
    },
  },
};
</script>
